import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AppShell } from "./AppShell";
import {
  subscribeCanonicalStageChanged,
  type CanonicalStageChangedDetail,
} from "./canonicalStageEvents";
import { PlaceholderRoute } from "./PlaceholderRoute";
import { RouteDeliveryBoundary } from "./RouteDeliveryBoundary";
import { isLabStageId, type LabStageId } from "./navigation";

const CommandDeck = lazy(() =>
  import("../features/command-deck/CommandDeck").then(({ CommandDeck }) => ({
    default: CommandDeck,
  })),
);
const ContractDesk = lazy(() =>
  import("../features/contract-desk/ContractDesk").then(({ ContractDesk }) => ({
    default: ContractDesk,
  })),
);
const AuctionDesk = lazy(() =>
  import("../features/auction-desk/AuctionDesk").then(({ AuctionDesk }) => ({
    default: AuctionDesk,
  })),
);
const VeteranAuctionDesk = lazy(() =>
  import("../features/veteran-auction-desk/VeteranAuctionDesk").then(
    ({ VeteranAuctionDesk }) => ({ default: VeteranAuctionDesk }),
  ),
);
const CanonicalGameDay = lazy(() =>
  import("../features/canonical-game-day/CanonicalGameDay").then(
    ({ CanonicalGameDay }) => ({ default: CanonicalGameDay }),
  ),
);
const SeasonDesk = lazy(() =>
  import("../features/season-desk/SeasonDesk").then(({ SeasonDesk }) => ({
    default: SeasonDesk,
  })),
);
const DraftDesk = lazy(() =>
  import("../features/draft-desk/DraftDesk").then(({ DraftDesk }) => ({
    default: DraftDesk,
  })),
);
const FaabDesk = lazy(() =>
  import("../features/faab-desk/FaabDesk").then(({ FaabDesk }) => ({
    default: FaabDesk,
  })),
);
const RfaDesk = lazy(() =>
  import("../features/rfa-desk/RfaDesk").then(({ RfaDesk }) => ({
    default: RfaDesk,
  })),
);
const TeamHq = lazy(() =>
  import("../features/team-hq/TeamHq").then(({ TeamHq }) => ({
    default: TeamHq,
  })),
);
const TradeDesk = lazy(() =>
  import("../features/trade-desk/TradeDesk").then(({ TradeDesk }) => ({
    default: TradeDesk,
  })),
);
const MultiTradeDesk = lazy(() =>
  import("../features/multi-trade-desk/MultiTradeDesk").then(
    ({ MultiTradeDesk }) => ({ default: MultiTradeDesk }),
  ),
);
const RehearsalDesk = lazy(() =>
  import("../features/rehearsal-desk/RehearsalDesk").then(({ RehearsalDesk }) => ({
    default: RehearsalDesk,
  })),
);
const JourneyDesk = lazy(() =>
  import("../features/journey-desk/JourneyDesk").then(({ JourneyDesk }) => ({
    default: JourneyDesk,
  })),
);
const TradeCenter = lazy(() =>
  import("../features/trades/TradeCenter").then(({ TradeCenter }) => ({
    default: TradeCenter,
  })),
);
const CapLab = lazy(() =>
  import("../features/cap-lab/CapLab").then(({ CapLab }) => ({
    default: CapLab,
  })),
);
const FixtureRuleDetail = lazy(() =>
  import("../features/cap-lab/FixtureRuleDetail").then(
    ({ FixtureRuleDetail }) => ({ default: FixtureRuleDetail }),
  ),
);
const FixtureTransitionTrace = lazy(() =>
  import("../features/cap-lab/FixtureTransitionTrace").then(
    ({ FixtureTransitionTrace }) => ({ default: FixtureTransitionTrace }),
  ),
);
const CommissionerControlTower = lazy(() =>
  import("../features/commissioner/CommissionerControlTower").then(
    ({ CommissionerControlTower }) => ({ default: CommissionerControlTower }),
  ),
);
const LaunchSimulation = lazy(() =>
  import("../features/simulation/LaunchSimulation").then(
    ({ LaunchSimulation }) => ({ default: LaunchSimulation }),
  ),
);
const DataHealth = lazy(() =>
  import("../features/data-health/DataHealth").then(({ DataHealth }) => ({
    default: DataHealth,
  })),
);
const MflConsole = lazy(() =>
  import("../features/commissioner-console/MflConsole").then(({ MflConsole }) => ({
    default: MflConsole,
  })),
);
const SignIn = lazy(() =>
  import("../features/auth/SignIn").then(({ SignIn }) => ({ default: SignIn })),
);
const Membership = lazy(() =>
  import("../features/commissioner-console/Membership").then(
    ({ Membership }) => ({ default: Membership }),
  ),
);
const MappingAdmin = lazy(() =>
  import("../features/commissioner-console/MappingAdmin").then(
    ({ MappingAdmin }) => ({ default: MappingAdmin }),
  ),
);
const MflMappings = lazy(() =>
  import("../features/commissioner-console/MflMappings").then(
    ({ MflMappings }) => ({ default: MflMappings }),
  ),
);
const IntegrityCenter = lazy(() =>
  import("../features/integrity/IntegrityCenter").then(
    ({ IntegrityCenter }) => ({ default: IntegrityCenter }),
  ),
);
const LeagueOffice = lazy(() =>
  import("../features/league-office/LeagueOffice").then(({ LeagueOffice }) => ({
    default: LeagueOffice,
  })),
);
const ScoringLab = lazy(() =>
  import("../features/scoring-lab/ScoringLab").then(({ ScoringLab }) => ({
    default: ScoringLab,
  })),
);
const PlayoffDesk = lazy(() =>
  import("../features/playoff-desk/PlayoffDesk").then(({ PlayoffDesk }) => ({
    default: PlayoffDesk,
  })),
);
const YearbookDesk = lazy(() =>
  import("../features/yearbook-desk/YearbookDesk").then(({ YearbookDesk }) => ({
    default: YearbookDesk,
  })),
);
const SettingsCatalog = lazy(() =>
  import("../features/settings/SettingsCatalog").then(
    ({ SettingsCatalog }) => ({ default: SettingsCatalog }),
  ),
);

const routeTitles: Readonly<Record<string, string>> = {
  "/sign-in": "Sign in",
  "/team/fixture-team": "Team HQ",
  "/market": "FAAB Waivers",
  "/market/rfa": "Restricted free agency",
  "/faab": "FAAB Waivers",
  "/trades": "Trades",
  "/trades/multi-team": "Multi-team trade",
  "/trade": "Trades",
  "/trades/fixture": "Trade room",
  "/rehearsal": "Rehearsal",
  "/journey": "Franchise year",
  "/contracts": "Contracts",
  "/season": "Season",
  "/auction": "Startup auction",
  "/auction/veterans": "Veteran auction",
  "/draft": "Rookie Draft",
  "/gameday": "Game Day",
  "/gameday/scoring-lab": "Scoring Lab",
  "/playoffs": "Playoffs",
  "/history": "History and yearbook",
  "/players": "Players",
  "/commissioner": "Commissioner Control Tower",
  "/commissioner/simulation": "Season Rehearsal",
  "/commissioner/data-health": "Data Health",
  "/commissioner/health": "Integrity Center",
  "/commissioner/mfl": "MFL Operations",
  "/commissioner/mfl/mappings": "MFL Franchise Mappings",
  "/commissioner/mfl/mappings/configure": "Configure Franchise Mappings",
  "/commissioner/membership": "Membership",
  "/commissioner/audit": "Audit",
  "/league/calendar": "League Calendar",
};

const normalizePath = (path: string): string => {
  const normalized = path.replace(/\/+$/, "");
  return normalized || "/";
};

export const App = () => {
  const path = normalizePath(window.location.pathname);
  const shellPath =
    path === "/trade" ? "/trades" : path === "/faab" ? "/market" : path;
  const [labStage, setLabStage] = useState<LabStageId | null>(null);
  const canonicalEventGenerationRef = useRef(0);
  const latestCanonicalEventVersionRef = useRef(-1);
  const latestCanonicalEventStageRef = useRef<LabStageId | null>(null);
  const stageLoadGenerationRef = useRef(0);

  const applyCanonicalStage = useCallback(
    ({ stage, stageVersion }: CanonicalStageChangedDetail): void => {
      if (
        stageVersion < latestCanonicalEventVersionRef.current ||
        (stageVersion === latestCanonicalEventVersionRef.current &&
          latestCanonicalEventStageRef.current !== null &&
          stage !== latestCanonicalEventStageRef.current)
      ) {
        return;
      }
      latestCanonicalEventVersionRef.current = stageVersion;
      latestCanonicalEventStageRef.current = stage;
      canonicalEventGenerationRef.current += 1;
      setLabStage(stage);
    },
    [],
  );

  useEffect(
    () => subscribeCanonicalStageChanged(applyCanonicalStage),
    [applyCanonicalStage],
  );

  useEffect(() => {
    let cancelled = false;
    const loadGeneration = stageLoadGenerationRef.current + 1;
    stageLoadGenerationRef.current = loadGeneration;
    const eventGenerationAtStart = canonicalEventGenerationRef.current;
    void (async () => {
      try {
        const response = await fetch("/api/v1/constitution-desk/state");
        const payload: unknown = await response.json();
        if (
          cancelled ||
          loadGeneration !== stageLoadGenerationRef.current ||
          eventGenerationAtStart !== canonicalEventGenerationRef.current ||
          payload === null ||
          typeof payload !== "object" ||
          !("data" in payload) ||
          payload.data === null ||
          typeof payload.data !== "object" ||
          !("stage" in payload.data) ||
          payload.data.stage === null ||
          typeof payload.data.stage !== "object" ||
          !("stage" in payload.data.stage) ||
          typeof payload.data.stage.stage !== "string" ||
          !("version" in payload.data.stage) ||
          typeof payload.data.stage.version !== "number"
        ) {
          return;
        }
        if (isLabStageId(payload.data.stage.stage)) {
          setLabStage(payload.data.stage.stage);
        }
      } catch {
        // Keep equal-weight navigation when the lab stage is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path]);

  let content: ReactNode;
  if (path === "/sign-in") {
    content = <SignIn />;
  } else if (path === "/" || path === "/league") {
    content = <CommandDeck />;
  } else if (path === "/team/fixture-team") {
    content = <TeamHq />;
  } else if (path === "/team/fixture-team/cap") {
    content = <CapLab />;
  } else if (path === "/trades" || path === "/trade") {
    content = <TradeDesk />;
  } else if (path === "/trades/multi-team") {
    content = <MultiTradeDesk />;
  } else if (path === "/rehearsal") {
    content = <RehearsalDesk />;
  } else if (path === "/journey") {
    content = <JourneyDesk />;
  } else if (path === "/trades/fixture") {
    content = <TradeCenter />;
  } else if (path === "/market" || path === "/faab") {
    content = <FaabDesk />;
  } else if (path === "/market/rfa") {
    content = <RfaDesk />;
  } else if (path === "/contracts") {
    content = <ContractDesk />;
  } else if (path === "/season") {
    content = <SeasonDesk />;
  } else if (path === "/auction") {
    content = <AuctionDesk />;
  } else if (path === "/auction/veterans") {
    content = <VeteranAuctionDesk />;
  } else if (path === "/draft") {
    content = <DraftDesk />;
  } else if (path === "/league/office") {
    content = <LeagueOffice />;
  } else if (path === "/league/rules/LC-CUT-FIXTURE-001") {
    content = <FixtureRuleDetail />;
  } else if (path === "/commissioner/audit") {
    content = <FixtureTransitionTrace />;
  } else if (path === "/commissioner/settings") {
    content = <SettingsCatalog />;
  } else if (path === "/commissioner") {
    content = <CommissionerControlTower />;
  } else if (path === "/commissioner/simulation") {
    content = <LaunchSimulation />;
  } else if (path === "/commissioner/data-health") {
    content = <DataHealth />;
  } else if (path === "/commissioner/health") {
    content = <IntegrityCenter />;
  } else if (path === "/commissioner/mfl") {
    content = <MflConsole />;
  } else if (path === "/commissioner/mfl/mappings") {
    content = <MflMappings />;
  } else if (path === "/commissioner/mfl/mappings/configure") {
    content = <MappingAdmin />;
  } else if (path === "/commissioner/membership") {
    content = <Membership />;
  } else if (path === "/gameday") {
    content = <CanonicalGameDay />;
  } else if (path === "/gameday/scoring-lab") {
    content = <ScoringLab />;
  } else if (path === "/playoffs") {
    content = <PlayoffDesk />;
  } else if (path === "/history") {
    content = <YearbookDesk />;
  } else {
    content = <PlaceholderRoute title={routeTitles[path] ?? "League Command"} />;
  }

  return (
    <AppShell path={shellPath} labStage={labStage}>
      <RouteDeliveryBoundary>
        <Suspense
          fallback={
            <section className="route-loading" role="status" aria-live="polite">
              <span aria-hidden />
              <p>Opening command surface…</p>
            </section>
          }
        >
          {content}
        </Suspense>
      </RouteDeliveryBoundary>
    </AppShell>
  );
};
